import { useEffect, useRef } from "react";

/**
 * Creates a sticky horizontal scrollbar that floats above the table footer.
 * Syncs scroll position bidirectionally with the table container.
 */
export const useStickyScrollbar = (
  containerRef: React.RefObject<HTMLDivElement | null>
) => {
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const scrollbar = scrollbarRef.current;
    const inner = innerRef.current;
    if (!container || !scrollbar || !inner) return;

    const tableContainer = container.querySelector<HTMLElement>(".table-container");
    if (!tableContainer) return;

    let syncing = false;

    const syncScrollbarFromTable = () => {
      if (syncing) return;
      syncing = true;
      scrollbar.scrollLeft = tableContainer.scrollLeft;
      syncing = false;
    };

    const header = container.querySelector<HTMLElement>(".table-sticky-header");

    const syncTableFromScrollbar = () => {
      if (syncing) return;
      syncing = true;
      tableContainer.scrollLeft = scrollbar.scrollLeft;
      if (header) header.scrollLeft = scrollbar.scrollLeft;
      syncing = false;
    };

    const updateWidth = () => {
      // Match the inner div width to the table's scroll width
      const scrollWidth = tableContainer.scrollWidth;
      const clientWidth = tableContainer.clientWidth;
      inner.style.width = `${scrollWidth}px`;

      // Hide scrollbar if no overflow
      scrollbar.style.display = scrollWidth > clientWidth ? "block" : "none";
    };

    tableContainer.addEventListener("scroll", syncScrollbarFromTable);
    scrollbar.addEventListener("scroll", syncTableFromScrollbar);

    // Update width on resize
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(tableContainer);

    // Also observe mutations for column resize, data changes, etc.
    const mutationObserver = new MutationObserver(updateWidth);
    mutationObserver.observe(tableContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    updateWidth();

    return () => {
      tableContainer.removeEventListener("scroll", syncScrollbarFromTable);
      scrollbar.removeEventListener("scroll", syncTableFromScrollbar);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [containerRef]);

  return { scrollbarRef, innerRef };
};
